import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import HRHeader from "../components/HRHeader";
import api, { uploadFile } from "../api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileText, Upload, Download, Trash2, Plus, Building2, User } from "lucide-react";

const CATEGORIES = ["Contratto", "Certificazione", "Comunicazione", "Modulistica", "Documento"];
const fmt = (iso) => { try { return format(new Date(iso), "dd MMM yyyy", { locale: it }); } catch { return iso; } };

const downloadDoc = async (id, filename) => {
  try {
    const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "documento"; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch { toast.error("Errore nel download"); }
};

const DocumentiPage = () => {
  const { isAdmin } = useAuth();
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Contratto");
  const [assignee, setAssignee] = useState("all");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    const url = isAdmin ? "/documents" : "/documents/mine";
    api.get(url).then((r) => setList(r.data)).catch(() => {});
    if (isAdmin) api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Inserisci un titolo"); return; }
    if (!file) { toast.error("Allega un file (PDF o immagine)"); return; }
    setSubmitting(true);
    try {
      const up = await uploadFile(file);
      await api.post("/documents", {
        title, category, file_id: up.id,
        employee_id: assignee === "all" ? null : assignee,
      });
      toast.success("Documento caricato!");
      setTitle(""); setCategory("Contratto"); setAssignee("all"); setFile(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore nel caricamento"); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Documento eliminato"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="documenti-page">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><FileText size={24} /> Documenti</h1>
        <p className="text-[14px] text-gray-500 mb-6">Contratti, certificazioni e documenti aziendali.</p>

        <div className={`grid grid-cols-1 ${isAdmin ? "lg:grid-cols-3" : ""} gap-6`}>
          {isAdmin && (
            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-32">
                <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Plus size={18} /> Carica documento</h2>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Titolo</label>
                    <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title" placeholder="es. Contratto 2026" className="w-full border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] outline-none focus:border-[#16345c]" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Categoria</label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger className="w-full" data-testid="doc-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Destinatario</label>
                    <Select value={assignee} onValueChange={setAssignee}>
                      <SelectTrigger className="w-full" data-testid="doc-assignee"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutta l'azienda</SelectItem>
                        {employees.map((emp) => <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.matricola})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-sm px-3 py-2.5 text-[13px] cursor-pointer hover:border-[#16345c] transition-colors">
                    <Upload size={16} className="text-gray-400 shrink-0" />
                    <span className={`truncate ${file ? "text-[#16345c] font-semibold" : "text-gray-400"}`}>{file ? file.name : "Allega file (PDF o immagine)"}</span>
                    <input type="file" accept="application/pdf,image/*" className="hidden" data-testid="doc-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  </label>
                  <button type="submit" disabled={submitting} data-testid="doc-submit" className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide transition-colors disabled:opacity-60">
                    {submitting ? "Caricamento..." : "Carica documento"}
                  </button>
                </form>
              </div>
            </div>
          )}

          <div className={isAdmin ? "lg:col-span-2" : ""}>
            <h2 className="text-[16px] font-bold text-[#16345c] mb-4">{isAdmin ? "Tutti i documenti" : "I miei documenti"}</h2>
            {list.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
                <FileText size={40} className="mx-auto mb-3 opacity-40" /> Nessun documento disponibile
              </div>
            ) : (
              <div className="space-y-3" data-testid="doc-list">
                {list.map((d) => (
                  <div key={d.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-10 h-10 rounded-lg bg-[#16345c1a] text-[#16345c] flex items-center justify-center shrink-0"><FileText size={20} /></span>
                      <div className="min-w-0">
                        <div className="text-[14px] font-bold text-[#16345c] truncate">{d.title}</div>
                        <div className="text-[12px] text-gray-500 flex items-center gap-2 flex-wrap">
                          <span className="bg-gray-100 px-1.5 py-0.5 rounded text-[11px] font-semibold">{d.category}</span>
                          <span className="flex items-center gap-1">{d.employee_id ? <><User size={11} /> {d.employee_name}</> : <><Building2 size={11} /> Aziendale</>}</span>
                          <span>· {fmt(d.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => downloadDoc(d.id, d.filename)} data-testid={`doc-download-${d.id}`} className="flex items-center gap-1 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors"><Download size={14} /> Scarica</button>
                      {isAdmin && <button onClick={() => remove(d.id)} className="text-gray-400 hover:text-[#c00510] transition-colors"><Trash2 size={16} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocumentiPage;
